import { NestedTreeControl } from '@angular/cdk/tree';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, forwardRef, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { MatTreeModule, MatTreeNestedDataSource } from '@angular/material/tree';
import { Category } from '@interfaces/interfaces';

@Component({
  selector: 'app-category-tree',
  templateUrl: './category-tree.component.html',
  styleUrls: ['./category-tree.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CategoryTreeComponent),
      multi: true,
    },
  ],
  imports: [CommonModule, FormsModule, MatIconModule, MatRadioModule, MatTreeModule],
})
export class CategoryTreeComponent implements ControlValueAccessor {
  @Input() categories: Category[] = [];
  @Output() initialized = new EventEmitter<void>();

  treeControl = new NestedTreeControl<Category>((node) => node.children);
  dataSource = new MatTreeNestedDataSource<Category>();
  selectedCategory = signal<Category | null>(null);

  ngOnChanges() {
    this.dataSource.data = this.categories;
    this.initialized.emit();
  }

  private onChange = (value: Category | null) => {};
  private onTouched = () => {};

  private findNodePathById(id: number, nodes: Category[], path: Category[] = []): Category[] | null {
    for (const node of nodes) {
      const newPath = [...path, node];
      if (node.id === id) {
        return newPath;
      }
      if (node.children) {
        const result = this.findNodePathById(id, node.children, newPath);
        if (result) {
          return result;
        }
      }
    }
    return null;
  }

  expandToCategoryById(id: number): void {
    const path = this.findNodePathById(id, this.dataSource.data);
    if (path) {
      path.slice(0, -1).forEach((node) => this.treeControl.expand(node));
    }
  }

  isLeaf = (node: Category): boolean => !node.children || node.children.length === 0;

  hasChild = (_: number, node: Category) => !!node.children && node.children.length > 0;

  writeValue(value: Category | null): void {
    this.selectedCategory.set(value);
  }

  registerOnChange(fn: (value: Category | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Optional: implement if needed
  }

  onNodeClick(node: Category) {
    if (this.isLeaf(node)) {
      this.selectedCategory.set(node);
      this.onChange(node);
      this.onTouched();
    }
  }
}
